import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import Grid from "@material-ui/core/Grid";
import Checkbox from "@material-ui/core/Checkbox";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import {
  FiltroBase,
  FiltroHome,
  FiltroOperadora,
  FiltroUnidade,
  listaDocumentos,
  listaOperadoras,
  consultarLimitePeriodo,
  listarOperadoras,
  listarUnidades,
  PeriodoHome,
  TipoPainel,
} from "../../../../services/home";
import { AZUL_FUNDO, BORDA, ERRO_TEXTO, FUNDO_CARTAO, FONTE, NAVY, SUAVE, TEXTO_SOBRE_COR, estilos } from "../estilos/tokens";
import { acionavelPorTeclado } from "../componentes/acionavel";
import { PainelAncorado } from "../componentes/painelAncorado";
import { CampoData } from "../componentes/campoData";
import {
  MAX_DIAS_PERIODO,
  diasNoPeriodo,
  inicioMinimo,
  isoLocal,
  rotuloSemana,
  ultimoDiaUtil,
} from "./semana";
import { mascararDocumento, rotuloDeUnidade } from "./rotuloUnidade";

export const estiloInputData: React.CSSProperties = {
  fontFamily: FONTE,
  fontSize: 16,
  color: NAVY,
  background: FUNDO_CARTAO,
  border: `1px solid ${BORDA}`,
  borderRadius: 8,
  padding: "8px 10px",
};

export type AbaPeriodo = TipoPainel | "PREVISAO";

interface FiltroHomeContexto {
  filtro: FiltroBase;
  periodos: Partial<Record<AbaPeriodo, PeriodoHome>>;
  operadoras: FiltroOperadora[];
  unidades: FiltroUnidade[];
  maxDias: number;
  setOperadoras: (codOperadoras: number[]) => void;
  setCnpjs: (documentos: string[]) => void;
  setPeriodo: (aba: AbaPeriodo, inicio: string, fim: string) => void;
  semana: string;
  setSemana: (diaDaSemana: string) => void;
}

const Contexto = createContext<FiltroHomeContexto | null>(null);

export const FiltroHomeProvider = ({ children }: { children: React.ReactNode }) => {
  const [filtro, setFiltro] = useState<FiltroBase>({ operadoras: [], cnpjs: [] });
  const [periodos, setPeriodos] = useState<Partial<Record<AbaPeriodo, PeriodoHome>>>({});
  const [operadoras, setOperadoras] = useState<FiltroOperadora[]>([]);
  const [unidades, setUnidades] = useState<FiltroUnidade[]>([]);
  const [maxDias, setMaxDias] = useState(MAX_DIAS_PERIODO);
  const [semana, setSemana] = useState(() => isoLocal(new Date()));

  useEffect(() => {
    let ativo = true;
    listarOperadoras()
      .then((lista) => ativo && setOperadoras(lista))
      .catch(() => ativo && setOperadoras([]));
    listarUnidades()
      .then((lista) => ativo && setUnidades(lista))
      .catch(() => ativo && setUnidades([]));
    consultarLimitePeriodo()
      .then((dias) => ativo && setMaxDias(dias))
      .catch(() => undefined);
    return () => {
      ativo = false;
    };
  }, []);

  const valor = useMemo<FiltroHomeContexto>(
    () => ({
      filtro,
      periodos,
      operadoras,
      unidades,
      maxDias,
      setOperadoras: (codOperadoras) =>
        setFiltro((atual) => ({ ...atual, operadoras: listaOperadoras(codOperadoras) })),
      setCnpjs: (documentos) => setFiltro((atual) => ({ ...atual, cnpjs: listaDocumentos(documentos) })),
      setPeriodo: (aba, inicio, fim) => setPeriodos((atuais) => ({ ...atuais, [aba]: { inicio, fim } })),
      semana,
      setSemana,
    }),
    [filtro, periodos, operadoras, unidades, maxDias, semana]
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
};

export const useFiltroHome = (): FiltroHomeContexto => {
  const contexto = useContext(Contexto);
  if (!contexto) {
    throw new Error("useFiltroHome deve ser usado dentro de FiltroHomeProvider.");
  }
  return contexto;
};

export const useFiltroAba = (aba: AbaPeriodo): FiltroHome => {
  const { filtro, periodos } = useFiltroHome();
  const periodo = periodos[aba];
  const { operadoras, cnpjs } = filtro;
  const dia = ultimoDiaUtil(new Date());
  const inicio = periodo ? periodo.inicio : dia;
  const fim = periodo ? periodo.fim : dia;

  return useMemo(() => ({ operadoras, cnpjs, inicio, fim }), [operadoras, cnpjs, inicio, fim]);
};

const formatarData = (iso: string) => {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
};

const Chip = ({
  rotulo,
  valor,
  aberto,
  aoAbrir,
  ajuda,
}: {
  rotulo: string;
  valor: string;
  aberto: boolean;
  aoAbrir: (evento: React.SyntheticEvent<HTMLElement>) => void;
  ajuda?: string;
}) => (
  <div style={estilos.filtro}>
    <span style={{ ...estilos.filtroRotulo, display: "inline-flex", alignItems: "center", gap: 4 }}>
      {rotulo}
      {ajuda && (
        <Tooltip
          title={
            <span data-teste="texto-ajuda-filtro" style={estilos.textoAjudaIndicador}>
              {ajuda}
            </span>
          }
          arrow
          placement="top"
        >
          <InfoOutlined aria-label={ajuda} aria-hidden={false} role="img" style={{ fontSize: 17, color: SUAVE }} />
        </Tooltip>
      )}
    </span>
    <span
      style={{ ...estilos.filtroValor, cursor: "pointer" }}
      aria-haspopup="true"
      aria-expanded={aberto}
      aria-label={`${rotulo}: ${valor}`}
      {...acionavelPorTeclado(aoAbrir)}
    >
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{valor}</span>
      <KeyboardArrowDownIcon style={{ fontSize: 23, flexShrink: 0 }} />
    </span>
  </div>
);

interface OpcaoSelecao<T> {
  valor: T;
  rotulo: string;
  termosBusca?: string;
}

const compactar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const ConteudoSelecaoMultipla = <T extends string | number>({
  rotuloTodos,
  rotuloBusca,
  rotuloSemResultado,
  opcoes,
  marcados,
  setMarcados,
  aoAplicar,
}: {
  rotuloTodos: string;
  rotuloBusca: string;
  rotuloSemResultado: string;
  opcoes: OpcaoSelecao<T>[];
  marcados: T[];
  setMarcados: (atualizar: (atual: T[]) => T[]) => void;
  aoAplicar: (todosMarcados: boolean) => void;
}) => {
  const [busca, setBusca] = useState("");
  const termo = compactar(busca);
  const visiveis = termo
    ? opcoes.filter((o) => compactar(`${o.rotulo} ${o.termosBusca || ""}`).indexOf(termo) >= 0)
    : opcoes;

  const todosMarcados = marcados.length === opcoes.length;
  const alternar = (valor: T) =>
    setMarcados((atual) => (atual.indexOf(valor) < 0 ? atual.concat([valor]) : atual.filter((v) => v !== valor)));
  const alternarTodos = () => setMarcados(() => (todosMarcados ? [] : opcoes.map((o) => o.valor)));

  const item = (rotulo: string, marcado: boolean, aoClicar: () => void, chave: string | number) => (
    <MenuItem key={chave} selected={marcado} onClick={aoClicar}>
      <Checkbox
        checked={marcado}
        tabIndex={-1}
        disableRipple={true}
        style={{ padding: 0, marginRight: 10 }}
        inputProps={{ "aria-label": rotulo } as any}
      />
      {rotulo}
    </MenuItem>
  );

  return (
    <>
      <div style={{ padding: "12px 16px 4px" }}>
        <input
          type="text"
          aria-label={rotuloBusca}
          placeholder={rotuloBusca}
          value={busca}
          autoFocus={true}
          onChange={(e) => setBusca(e.target.value)}
          style={{ ...estiloInputData, width: "100%", boxSizing: "border-box" }}
        />
      </div>
      <MenuList>
        {!termo && item(rotuloTodos, todosMarcados, alternarTodos, "__todos")}
        {visiveis.map((o) => item(o.rotulo, marcados.indexOf(o.valor) >= 0, () => alternar(o.valor), o.valor))}
        {visiveis.length === 0 && (
          <div style={{ padding: "8px 16px", fontFamily: FONTE, fontSize: 14, color: SUAVE }}>{rotuloSemResultado}</div>
        )}
        <div style={{ padding: "10px 16px 4px" }}>
          <Button
            variant="contained"
            fullWidth={true}
            disabled={marcados.length === 0}
            onClick={() => aoAplicar(todosMarcados)}
            style={{ background: AZUL_FUNDO, color: TEXTO_SOBRE_COR, opacity: marcados.length === 0 ? 0.5 : 1, fontFamily: FONTE, fontWeight: 500, textTransform: "none" }}
          >
            Aplicar
          </Button>
        </div>
      </MenuList>
    </>
  );
};

const MenuSelecaoMultipla = <T extends string | number>({
  ancora,
  aoFechar,
  ...conteudo
}: {
  ancora: HTMLElement | null;
  aoFechar: () => void;
  rotuloTodos: string;
  rotuloBusca: string;
  rotuloSemResultado: string;
  opcoes: OpcaoSelecao<T>[];
  marcados: T[];
  setMarcados: (atualizar: (atual: T[]) => T[]) => void;
  aoAplicar: (todosMarcados: boolean) => void;
}) => (
  <PainelAncorado ancora={ancora} aoFechar={aoFechar}>
    <ConteudoSelecaoMultipla {...conteudo} />
  </PainelAncorado>
);

const ajudaPeriodo = (maxDias: number) => `A consulta é limitada em ${maxDias} dias`;

export const FiltrosPainel = ({
  aba = "VENDA",
  semanal = false,
  vendasAte = false,
}: {
  aba?: AbaPeriodo;
  semanal?: boolean;
  vendasAte?: boolean;
}) => {
  const { filtro, operadoras, unidades, maxDias, setOperadoras, setCnpjs, setPeriodo, semana, setSemana } = useFiltroHome();
  const periodoAba = useFiltroAba(aba);
  const hoje = isoLocal(new Date());

  const [ancoraOperadora, setAncoraOperadora] = useState<HTMLElement | null>(null);
  const [ancoraUnidade, setAncoraUnidade] = useState<HTMLElement | null>(null);
  const [ancoraPeriodo, setAncoraPeriodo] = useState<HTMLElement | null>(null);
  const [inicioEdicao, setInicioEdicao] = useState("");
  const [fimEdicao, setFimEdicao] = useState("");
  const [operadorasEdicao, setOperadorasEdicao] = useState<number[]>(filtro.operadoras);
  const [cnpjsEdicao, setCnpjsEdicao] = useState<string[]>(filtro.cnpjs);
  const [ancoraSemana, setAncoraSemana] = useState<HTMLElement | null>(null);
  const [semanaEdicao, setSemanaEdicao] = useState(semana);

  const nomeOperadora = (codOperadora: number) => {
    const encontrada = operadoras.find((o) => o.codOperadora === codOperadora);
    return encontrada ? encontrada.nome : `Operadora ${codOperadora}`;
  };

  const rotuloOperadora =
    filtro.operadoras.length === 0
      ? "Todas"
      : filtro.operadoras.length === 1
      ? nomeOperadora(filtro.operadoras[0])
      : `${filtro.operadoras.length} operadoras`;

  const abrirOperadora = (evento: React.SyntheticEvent<HTMLElement>) => {
    if (ancoraOperadora) {
      setAncoraOperadora(null);
      return;
    }
    setOperadorasEdicao(filtro.operadoras);
    setAncoraOperadora(evento.currentTarget);
  };

  const aplicarOperadoras = (todasMarcadas: boolean) => {
    setOperadoras(todasMarcadas ? [] : operadorasEdicao);
    setAncoraOperadora(null);
  };

  const nomeUnidade = (documento: string) => {
    const encontrada = unidades.find((u) => u.documento === documento);
    return encontrada ? rotuloDeUnidade(encontrada) : mascararDocumento(documento);
  };

  const rotuloUnidade =
    filtro.cnpjs.length === 0
      ? "Todos"
      : filtro.cnpjs.length === 1
      ? nomeUnidade(filtro.cnpjs[0])
      : `${filtro.cnpjs.length} unidades`;

  const abrirUnidade = (evento: React.SyntheticEvent<HTMLElement>) => {
    if (ancoraUnidade) {
      setAncoraUnidade(null);
      return;
    }
    setCnpjsEdicao(filtro.cnpjs);
    setAncoraUnidade(evento.currentTarget);
  };

  const aplicarUnidades = (todasMarcadas: boolean) => {
    setCnpjs(todasMarcadas ? [] : cnpjsEdicao);
    setAncoraUnidade(null);
  };

  const abrirPeriodo = (evento: React.SyntheticEvent<HTMLElement>) => {
    if (ancoraPeriodo) {
      setAncoraPeriodo(null);
      return;
    }
    setInicioEdicao(periodoAba.inicio);
    setFimEdicao(periodoAba.fim);
    setAncoraPeriodo(evento.currentTarget);
  };

  const periodoIncompleto = !inicioEdicao || !fimEdicao || inicioEdicao > fimEdicao;
  const periodoLongo = !periodoIncompleto && diasNoPeriodo(inicioEdicao, fimEdicao) > maxDias;

  const aplicarPeriodo = () => {
    setPeriodo(aba, inicioEdicao, fimEdicao);
    setAncoraPeriodo(null);
  };

  const aplicarVendasAte = () => {
    const inicioBase = periodoAba.inicio;
    const inicioLimitado = inicioBase < inicioMinimo(fimEdicao, maxDias) ? inicioMinimo(fimEdicao, maxDias) : inicioBase;
    setPeriodo(aba, inicioLimitado > fimEdicao ? fimEdicao : inicioLimitado, fimEdicao);
    setAncoraPeriodo(null);
  };

  const abrirSemana = (evento: React.SyntheticEvent<HTMLElement>) => {
    if (ancoraSemana) {
      setAncoraSemana(null);
      return;
    }
    setSemanaEdicao(semana);
    setAncoraSemana(evento.currentTarget);
  };

  const aplicarSemana = () => {
    setSemana(semanaEdicao);
    setAncoraSemana(null);
  };

  const rotuloData: React.CSSProperties = {
    fontFamily: FONTE,
    fontSize: 14,
    fontWeight: 500,
    color: SUAVE,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
  };

  return (
    <Grid container spacing={2} style={{ marginBottom: 26 }}>
      <Grid item xs={12} md={4}>
        <Chip
          rotulo="Operadora"
          valor={rotuloOperadora}
          aberto={Boolean(ancoraOperadora)}
          aoAbrir={abrirOperadora}
        />
        <MenuSelecaoMultipla
          ancora={ancoraOperadora}
          aoFechar={() => setAncoraOperadora(null)}
          rotuloTodos="Todas"
          rotuloBusca="Buscar operadora"
          rotuloSemResultado="Nenhuma operadora encontrada"
          opcoes={operadoras.map((o) => ({ valor: o.codOperadora, rotulo: o.nome }))}
          marcados={operadorasEdicao}
          setMarcados={setOperadorasEdicao}
          aoAplicar={aplicarOperadoras}
        />
      </Grid>

      {semanal ? (
        <Grid item xs={12} md={4}>
          <Chip
            rotulo="Semana"
            valor={rotuloSemana(semana)}
            aberto={Boolean(ancoraSemana)}
            aoAbrir={abrirSemana}
          />
          <PainelAncorado ancora={ancoraSemana} aoFechar={() => setAncoraSemana(null)}>
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14, minWidth: 240 }}>
              <div>
                <div style={rotuloData}>Dia da semana</div>
                <CampoData rotulo="Dia da semana" valor={semanaEdicao} max={hoje} aoMudar={setSemanaEdicao} estilo={estiloInputData} />
              </div>
              {semanaEdicao && <div style={{ ...rotuloData, textTransform: "none" }}>{rotuloSemana(semanaEdicao)}</div>}
              <Button
                variant="contained"
                onClick={aplicarSemana}
                disabled={!semanaEdicao}
                style={{ background: AZUL_FUNDO, color: TEXTO_SOBRE_COR, fontFamily: FONTE, fontWeight: 500, textTransform: "none" }}
              >
                Aplicar
              </Button>
            </div>
          </PainelAncorado>
        </Grid>
      ) : vendasAte ? (
        <Grid item xs={12} md={4}>
          <Chip
            rotulo="Vendas até"
            valor={formatarData(periodoAba.fim)}
            aberto={Boolean(ancoraPeriodo)}
            aoAbrir={abrirPeriodo}
          />
          <PainelAncorado ancora={ancoraPeriodo} aoFechar={() => setAncoraPeriodo(null)}>
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14, minWidth: 240 }}>
              <div>
                <div style={rotuloData}>Vendas até</div>
                <CampoData rotulo="Vendas até" valor={fimEdicao} max={hoje} aoMudar={setFimEdicao} estilo={estiloInputData} />
              </div>
              <Button
                variant="contained"
                onClick={aplicarVendasAte}
                disabled={!fimEdicao}
                style={{ background: AZUL_FUNDO, color: TEXTO_SOBRE_COR, fontFamily: FONTE, fontWeight: 500, textTransform: "none" }}
              >
                Aplicar
              </Button>
            </div>
          </PainelAncorado>
        </Grid>
      ) : (
        <Grid item xs={12} md={4}>
          <Chip
            rotulo="Período"
            valor={`${formatarData(periodoAba.inicio)} a ${formatarData(periodoAba.fim)}`}
            aberto={Boolean(ancoraPeriodo)}
            aoAbrir={abrirPeriodo}
            ajuda={ajudaPeriodo(maxDias)}
          />
          <PainelAncorado ancora={ancoraPeriodo} aoFechar={() => setAncoraPeriodo(null)}>
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14, minWidth: 240 }}>
              <div>
                <div style={rotuloData}>Início</div>
                <CampoData rotulo="Início" valor={inicioEdicao} max={hoje} aoMudar={setInicioEdicao} estilo={estiloInputData} />
              </div>
              <div>
                <div style={rotuloData}>Fim</div>
                <CampoData rotulo="Fim" valor={fimEdicao} max={hoje} aoMudar={setFimEdicao} estilo={estiloInputData} />
              </div>
              {periodoLongo && (
                <div style={{ ...rotuloData, color: ERRO_TEXTO }}>
                  {ajudaPeriodo(maxDias)}.
                </div>
              )}
              <Button
                variant="contained"
                onClick={aplicarPeriodo}
                disabled={periodoIncompleto || periodoLongo}
                style={{ background: AZUL_FUNDO, color: TEXTO_SOBRE_COR, fontFamily: FONTE, fontWeight: 500, textTransform: "none" }}
              >
                Aplicar
              </Button>
            </div>
          </PainelAncorado>
        </Grid>
      )}

      <Grid item xs={12} md={4}>
        <Chip
          rotulo="Unidade"
          valor={rotuloUnidade}
          aberto={Boolean(ancoraUnidade)}
          aoAbrir={abrirUnidade}
        />
        <MenuSelecaoMultipla
          ancora={ancoraUnidade}
          aoFechar={() => setAncoraUnidade(null)}
          rotuloTodos="Todos"
          rotuloBusca="Buscar unidade"
          rotuloSemResultado="Nenhuma unidade encontrada"
          opcoes={unidades.map((u) => ({ valor: u.documento, rotulo: rotuloDeUnidade(u), termosBusca: u.documento }))}
          marcados={cnpjsEdicao}
          setMarcados={setCnpjsEdicao}
          aoAplicar={aplicarUnidades}
        />
      </Grid>
    </Grid>
  );
};
